import { map, type Observable } from 'rxjs';
import type { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryListItemResponse } from '@core/api/workspace/models/dataLibraryListItemResponse';
import type { SystemVariableItemResponse } from '@core/api/workspace/models/systemVariableItemResponse';
import type { VariablePickerResult } from '../components/variable-picker-overlay/variable-picker-overlay';
import type { BoundValue, BuiltForm, FormField } from '../models/built-form';
import {
  hasBoundTextTokens,
  resolveBoundTextTemplate,
} from './bound-text-template';

export function formatDataLibraryValue(
  value: string | number | boolean | null | undefined,
): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

/** Load name + value for a linked data-library variable id. */
export function resolveLinkedVariable(
  api: WorkspaceAPIService,
  dataLibraryId: string,
  fallbackValueType: VariablePickerResult['valueType'],
): Observable<VariablePickerResult | null> {
  return api
    .listDataLibrary({
      pageNumber: 1,
      pageSize: 100,
    })
    .pipe(
      map((response) => {
        const item = (response.data?.items ?? []).find(
          (entry) => entry.id === dataLibraryId && entry.kind === 'variable',
        );
        if (!item) {
          return null;
        }
        return toPickerResult(item, fallbackValueType);
      }),
    );
}

/** Stamp fresh system variable preview values onto field defaults (preview / handoff). */
export function enrichBuiltFormSystemDefaults(
  form: BuiltForm,
  catalog: SystemVariableItemResponse[],
): BuiltForm {
  const previewByKey = new Map(
    catalog.map((item) => [item.key, item.previewValue] as const),
  );

  return {
    ...form,
    pages: form.pages.map((page) => ({
      ...page,
      fields: page.fields.map((field) =>
        enrichFieldSystemDefault(field, previewByKey, catalog),
      ),
    })),
  };
}

function enrichFieldSystemDefault(
  field: FormField,
  previewByKey: Map<string, SystemVariableItemResponse['previewValue']>,
  catalog: SystemVariableItemResponse[],
): FormField {
  const bound = field.default;
  if (bound?.source !== 'system') {
    return field;
  }
  if (!previewByKey.has(bound.systemKey)) {
    return field;
  }
  const meta = catalog.find((item) => item.key === bound.systemKey);
  const preview = previewByKey.get(bound.systemKey);
  const nextDefault: BoundValue = {
    source: 'system',
    systemKey: bound.systemKey,
    label: bound.label ?? meta?.label ?? bound.systemKey,
    key: bound.key ?? bound.systemKey,
    value: preview ?? null,
  };
  return { ...field, default: nextDefault };
}

/** Attach resolved variable values onto form field defaults for canvas preview. */
export function enrichBuiltFormVariableDefaults(
  form: BuiltForm,
  items: DataLibraryListItemResponse[],
): BuiltForm {
  const byId = new Map(
    items
      .filter((item) => item.kind === 'variable')
      .map((item) => [item.id, item] as const),
  );

  return {
    ...form,
    pages: form.pages.map((page) => ({
      ...page,
      fields: page.fields.map((field) => enrichFieldDefault(field, byId)),
    })),
  };
}

/**
 * Resolve {{v.*}} / {{s.*}} in heading/paragraph labels for preview display.
 * Do not use on builder edit state — that must keep templates for save.
 */
export function enrichBuiltFormLabelTemplates(
  form: BuiltForm,
  catalog: SystemVariableItemResponse[],
  items: DataLibraryListItemResponse[],
): BuiltForm {
  const systemByKey = new Map(
    catalog.map((item) => [item.key, item.previewValue] as const),
  );
  const variableByKey = new Map(
    items
      .filter((item) => item.kind === 'variable' && item.key)
      .map((item) => [item.key, item.value ?? null] as const),
  );

  return {
    ...form,
    pages: form.pages.map((page) => ({
      ...page,
      fields: page.fields.map((field) => {
        if (field.type !== 'heading' && field.type !== 'paragraph') {
          return field;
        }
        if (!hasBoundTextTokens(field.label)) {
          return field;
        }
        return {
          ...field,
          label: resolveBoundTextTemplate(field.label, (namespace, key) => {
            if (namespace === 'v') {
              return variableByKey.has(key)
                ? (variableByKey.get(key) ?? null)
                : null;
            }
            if (namespace === 's') {
              return systemByKey.has(key)
                ? (systemByKey.get(key) ?? null)
                : null;
            }
            return '';
          }),
        };
      }),
    })),
  };
}

function enrichFieldDefault(
  field: FormField,
  byId: Map<string, DataLibraryListItemResponse>,
): FormField {
  const bound = field.default;
  if (bound?.source !== 'variable') {
    return field;
  }
  const item = byId.get(bound.dataLibraryId);
  if (!item) {
    return field;
  }
  const nextDefault: BoundValue = {
    source: 'variable',
    dataLibraryId: bound.dataLibraryId,
    label: item.name,
    key: item.key,
    value: item.value ?? null,
  };
  return { ...field, default: nextDefault };
}

function toPickerResult(
  item: DataLibraryListItemResponse,
  fallbackValueType: VariablePickerResult['valueType'],
): VariablePickerResult {
  const valueType =
    item.valueType === 'text' ||
    item.valueType === 'number' ||
    item.valueType === 'boolean' ||
    item.valueType === 'date'
      ? item.valueType
      : fallbackValueType;
  return {
    id: item.id,
    name: item.name,
    key: item.key,
    valueType,
    value: item.value ?? null,
    valueLabel: formatDataLibraryValue(item.value),
  };
}
